import React from 'react';
import get from 'lodash/get';

import propsTransformer from './propsTransformer';
import getCTAProps from '@utils/cta';
import getFluidImage from '@utils/fluidImage';
import Button from '@components/Button';
import AudioBlock from '@components/block/Audio';
import Carousel from '@components/block/Carousel';
import ContactBlock from '@components/block/Contact';
import ContentCarousel from '@components/block/ContentCarousel';
import CourseCarousel from '@components/block/CourseCarousel';
import Container from '@components/Container';
import DonationForm from '@components/DonationForm';
import DonationItemsForm from '@components/DonationItemsForm';
import EmbedBlock from '@components/block/Embed';
import FullWidthImageBlock from '@components/block/FullWidthImage';
import HorizonPicker from '@components/HorizonPicker';
import ImageBlock from '@components/block/Image';
import DoubleImageBlock from '@components/block/DoubleImage';
import Instagram from '@components/Instagram';
import MapBlock from '@components/block/Map';
import MediaGalleryPreview from '@components/MediaGallery/Preview';
import MembersBlock from '@components/block/Members';
import PageBlock from '@components/block/Page';
import ChatBlock from '@components/block/Chat';
import ScheduleBlock from '@components/block/Schedule';
import Section from '@components/Section';
import StaffBlock from '@components/block/Staff';
import StoriesBlock from '@components/block/Stories';
import StudentStories from '@components/StudentStories';
import Testimonial from '@components/block/Testimonial';
import TuitionCalculator from '@components/TuitionCalculator';
import TextBlock from '@components/block/Text';
import VideoEmbedBlock from '@components/block/VideoEmbed';
import UpNext from '@components/block/UpNext';
import FAQ from '@components/FAQ';
import SocialMediaEmbed from '@components/SocialMediaEmbed';
import SocialMediaListing from '@components/SocialMediaListing';
import QuickFacts from '@components/block/QuickFacts';
import RoundedSimpleCards from '@components/block/RoundedSimpleCards';
import SimpleCards from '@components/block/SimpleCards';
import Stories2Sides from '@components/block/Stories2Sides';
import SocialMediaSimpleListing from '@components/SocialMediaSimpleListing';
import ImageTextBlock from '@components/block/ImageTextBlock';
import VideoTextBlock from '@components/block/VideoTextBlock';
import PersonBubbleBlock from '@components/block/PersonBubbleBlock';
import DecoratedBubbleBlock from '@components/block/DecoratedBubbleBlock';
import HorizonsChart from '@components/block/HorizonsChart';
import CircleChart from '@components/block/CircleChart';

function renderBlock(block, parent, lang) {
  if (!block) {
    return null;
  }
  block = propsTransformer(block);

  switch (block._type) {
    case 'audioBlock': {
      const file = {
        en: block.file,
        vi: block.viFile,
        zh_Hans: block.zh_HansFile,
      };

      const translatedBlock = {
        ...block,
        file: file[lang] || file.en,
      };

      return (
        <>
          <AudioBlock key={block._key} {...translatedBlock} />
        </>
      );
    }
    case 'carousel': {
      return (
        <>
          <Carousel key={block._key} {...block} />
        </>
      );
    }
    case 'contactBlock': {
      return (
        <>
          <ContactBlock key={block._key} {...block} />
        </>
      );
    }
    case 'contentCarousel': {
      return (
        <>
          <ContentCarousel key={block._key} {...block} />
        </>
      );
    }
    case 'courseCarousel': {
      const props = {
        ...block,
        items: Array.isArray(block.items)
          ? block.items.map((item) => ({
            ...item,
            image: {
              asset: {
                fluid: getFluidImage(item.imageExtended.image, { sizes: '(max-width: 600px) 100vw, 50vw' }),
              },
            },
          }))
          : [],
      };

      return (
        <>
          <CourseCarousel key={block._key} {...props} />
        </>
      );
    }
    case 'cta': {
      return (
        <>
          <Container centered columns={[12, 5]}>
            <div style={{ textAlign: 'center' }}>
              <Button appearance={block.colorTheme} fill {...getCTAProps(block)}>
                {block.label}
              </Button>
            </div>
          </Container>
        </>
      );
    }
    case 'donationBlock': {
      return (
        <>
          <DonationForm key={block._key} {...block} />
        </>
      );
    }
    case 'donationItemsBlock': {
      return (
        <>
          <DonationItemsForm key={block._key} {...block} />
        </>
      );
    }
    case 'embedBlock': {
      return (
        <>
          <EmbedBlock key={block._key} {...block} />
        </>
      );
    }
    case 'fullWidthImageBlock': {
      const props = {
        ...block,
        image: {
          asset: {
            fluid: getFluidImage(block.imageExtended.image, { sizes: '(max-width: 600px) 100vw, 50vw' }),
          },
        },
      };

      return (
        <>
          <FullWidthImageBlock key={block._key} {...props} />
        </>
      );
    }
    case 'gallery': {
      const props = {
        ...block,
        // images: get(block, 'images', []).map(({ image }) => ({
        //   height: get(image, 'asset.metadata.dimensions.height'),
        //   src: get(image, 'asset.url'),
        //   width: get(image, 'asset.metadata.dimensions.width'),
        // })),
        images: get(block, 'imagesExtended', []).map(({ altText, image }) => {
          const width = 600;
          const aspectRatio = get(image, 'asset.metadata.dimensions.aspectRatio');
          const height = parseInt(width / aspectRatio);

          return {
            height, width,
            src: `${get(image, 'asset.url')}?w=${width}&h=${height}`,
            alt: altText,
          };
        }),
      };

      return (
        <>
          <MediaGalleryPreview key={block._key} {...props} />
        </>
      );
    }
    case 'horizonPicker': {
      const placeholders = {
        en: block.placeholders,
        vi: block.viPlaceholders,
        zh_Hans: block.zh_HansPlaceholders,
      };

      const resultsIntro = {
        en: block.resultsIntro,
        vi: block.viResultsIntro,
        zh_Hans: block.zh_HansResultsIntro,
      };

      const translatedBlock = {
        ...block,
        placeholders: placeholders[lang] || placeholders.en,
        resultsIntro: resultsIntro[lang] || resultsIntro.en,
      };

      return (
        <>
          <HorizonPicker key={block._key} {...translatedBlock} />
        </>
      );
    }
    case 'imageExtended': {
      const props = {
        ...block,
        image: {
          asset: {
            fluid: getFluidImage(block.image, { sizes: '(max-width: 600px) 100vw, 30vw' }),
          },
        },
      };

      return (
        <>
          <ImageBlock key={block._key} {...props} />
        </>
      );
    }
    case 'doubleImageBlock': {
      const props = {
        ...block,
        image1: {
          asset: {
            fluid: getFluidImage(block.imageExtended1.image, { sizes: '(max-width: 600px) 100vw, 30vw' }),
          },
        },
        image2: {
          asset: {
            fluid: getFluidImage(block.imageExtended2.image, { sizes: '(max-width: 600px) 100vw, 30vw' }),
          },
        },
      };

      return (
        <>
          <DoubleImageBlock key={block._key} {...props} />
        </>
      );
    }
    case 'instagramBlock': {
      return (
        <>
          <Instagram key={block._key} {...block} />
        </>
      );
    }
    case 'map':
      return (
        <>
          <MapBlock key={block._key} {...block} />
        </>
      );
    case 'membersBlock':
      return (
        <>
          <MembersBlock key={block._key} {...block} />
        </>
      );
    case 'pageBlock': {
      const props = {
        ...block,
        imagesExtended: block.imagesExtended.map((image) => ({
          ...image,
          asset: {
            fluid: getFluidImage(image.image, {
              sizes: '(max-width: 600px) 100vw, 50vw',
            }),
          },
        })),
      };

      return (
        <>
          <PageBlock key={block.key} {...props} />
        </>
      );
    }
    case 'chatBlock': {
      const props = {
        ...block,
        image: {
          asset: {
            fluid: getFluidImage(block.imageExtended.image, {
              sizes: '(max-width: 600px) 100vw, 50vw',
            }),
          },
        },
      };

      return (
        <>
          <ChatBlock key={block.key} {...props} />
        </>
      );
    }
    case 'scheduleBlock': {
      const getDescription = (item, lang) => {
        switch (lang) {
          case 'zh_Hans':
            return item['zh_HansDescription'];
          case 'vi':
            return item['viDescription'];
          default:
            return item['description'];
        }
      };
      const items = block.items.map(item => ({
        ...item,
        description: getDescription(item, lang),
      }));

      return (
        <>
          <ScheduleBlock key={block._key} {...block} items={items} />
        </>
      );
    }
    case 'staffBlock':
      return (
        <>
          <StaffBlock key={block._key} {...block} />
        </>
      );
    case 'storiesBlock':
      return (
        <>
          <StoriesBlock key={block._key} {...block} />
        </>
      );
    case 'studentStories':
      const props = {
        ...block,
        items: Array.isArray(block.items)
          ? block.items.map((item) => ({
            ...item,
            student: {
              ...item.student,
              image: {
                asset: {
                  fluid: getFluidImage(get(item, 'student.imageExtended.image'), {
                    sizes: '(max-width: 600px) 100vw, 20vw',
                  }),
                },
              },
            },
          }))
          : [],
      };

      return (
        <>
          <StudentStories key={block._key} {...props} />
        </>
      );
    case 'textBlock': {
      const text = {
        en: block.text,
        zh_Hans: block.zh_HansText,
        vi: block.viText,
      };

      return (
        <>
          <TextBlock
            key={block._key}
            parent={parent}
            sidebar={block.sidebar}
            anchorName={block.anchorName}
            columns={block.columns}
            text={text[lang] || text.en}
            lang={lang}
          />
        </>

      );
    }
    case 'testimonial': {
      const props = {
        ...block,
        locale: lang,
        image: {
          asset: {
            fluid: getFluidImage(block.imageExtended?.image, {
              sizes: '(max-width: 600px) 20vw, 20vw',
            }),
          },
        },
      };

      return (
        <>
          <Testimonial key={block._key} {...props} />
        </>
      );
    }
    case 'tuitionCalculator':
      return (
        <>
          <TuitionCalculator key={block._key} {...block} />
        </>
      );
    case 'upNext': {
      const props = {
        ...block,
        items: Array.isArray(block.items)
          ? block.items.map((item) => ({
            ...item,
            image: {
              asset: {
                fluid: getFluidImage(item.imageExtended.image, { sizes: '(max-width: 600px) 100vw, 30vw' }),
              },
            },
          }))
          : [],
      };

      return (
        <>
          <UpNext key={block._key} {...props} />
        </>
      );
    }
    case 'videoEmbed': {
      return (
        <>
          <VideoEmbedBlock key={block._key} {...block} />
        </>
      );
    }
    case 'faqBlock': {
      return (
        <>
          <FAQ key={block._key} {...block} />
        </>
      );
    }
    case 'socialMediaEmbed': {
      return (
        <>
          <SocialMediaEmbed key={block._key} {...block} />
        </>
      );
    }
    case 'socialMediaListing': {
      return (
        <>
          <SocialMediaListing key={block._key} {...block} />
        </>
      );
    }
    case 'socialMediaSimpleListing': {
      const props = {
        ...block,
        socialMediaList: Array.isArray(block.socialMediaList)
          ? block.socialMediaList.map((item) => ({
            ...item,
            image: {
              asset: {
                fluid: getFluidImage(item.image.image, { sizes: '(max-width: 600px) 100vw, 30vw' }),
              },
            },
          }))
          : [],
      };
      return (
        <>
          <SocialMediaSimpleListing key={props._key} {...props} />
        </>
      );
    }
    case 'quickFacts': {
      return (
        <>
          <QuickFacts key={block._key} {...block} />
        </>
      );
    }
    case 'roundedSimpleCards': {
      const props = {
        ...block,
        cards: Array.isArray(block.cards)
          ? block.cards.map((card) => ({
            ...card,
            ...(card.image?.image && {
              image: {
                asset: {
                  fluid: getFluidImage(card.image?.image, { sizes: '(max-width: 600px) 100vw, 50vw' }),
                },
              },
            }),
          })) : [],
      };

      return (
        <>
          <RoundedSimpleCards key={block._key}  {...props} />
        </>
      );
    }
    case 'stories2SidesBlock': {
      return (
        <>
          <Stories2Sides key={block._key} {...block} />
        </>
      );
    }
    case 'simpleCards': {
      const props = {
        ...block,
        cards: Array.isArray(block.cards)
          ? block.cards.map((card) => ({
            ...card,
            ...(card.image?.image && {
              image: {
                asset: {
                  fluid: getFluidImage(card.image?.image, { sizes: '(max-width: 600px) 100vw, 50vw' }),
                },
              },
            }),
          })) : [],
      };

      return (
        <>
          <SimpleCards key={block._key} {...props} />
        </>
      );
    }
    case 'videoTextBlock': {
      const text = {
        text: {
          en: block.text,
          zh_Hans: block.zh_HansText,
          vi: block.viText,
        },
      };

      return (
        <VideoTextBlock
          key={block._key}
          text={text[lang] || text.en}
          {...block}
        />
      );
    }
    case 'imageTextBlock': {
      const text = {
        text: {
          en: block.text,
          zh_Hans: block.zh_HansText,
          vi: block.viText,
        },
      };

      const props = {
        ...block,
        imagesExtended: (block.imagesExtended || []).map((image) => ({
          ...image,
          asset: {
            fluid: getFluidImage(image.image, {
              sizes: '(max-width: 600px) 100vw, 50vw',
            }),
          },
        })),
      };

      return (
        <ImageTextBlock
          key={block._key}
          text={text[lang] || text.en}
          {...props}
        />
      );
    }
    case 'personBubbleBlock': {
      const props = {
        ...block,
        items: Array.isArray(block.items)
          ? block.items.map((item) => {
            const isSimpleCard = item._type === 'simpleBubbleCardItem';

            return {
              ...item,
              ...(!isSimpleCard && item.imageExtended?.image && {
                imageExtended: {
                  image: {
                    asset: {
                      fluid: getFluidImage(item.imageExtended?.image, { sizes: '(max-width: 600px) 100vw, 50vw' }),
                    },
                  },
                },
              }),
              ...(isSimpleCard && item.image?.image?.asset && {
                image: {
                  asset: {
                    fluid: getFluidImage(item.image.image, {
                      sizes: '(max-width: 600px) 100vw, 50vw',
                    }),
                  },
                },
                altText: item.image.altText || '',
              }),
            };
          })
          : [],
      };

      return (
        <PersonBubbleBlock
          key={block._key}
          items={props.items}
          bubblesPerRow={block.bubblesPerRow}
        />
      );
    }
    case 'decoratedBubbleBlock': {
      const props = {
        ...block,
        items: Array.isArray(block.items)
          ? block.items.map((card) => ({
            ...card,
            ...(card.image?.image && {
              image: {
                asset: {
                  fluid: getFluidImage(card.image?.image, { sizes: '(max-width: 600px) 100vw, 50vw' }),
                },
              },
            }),
          })) : [],
      };

      return (
        <DecoratedBubbleBlock key={block._key}  {...props} />
      );
    }
    case 'horizonsChartBlock': {
      const grades = Array.isArray(block.grades)
        ? block.grades.map((grade) => ({
          ...grade,
          ...(grade.actionsImage?.image && {
            actionsImage: {
              asset: {
                fluid: getFluidImage(grade.actionsImage.image, { sizes: '(max-width: 600px) 100vw, 30vw' }),
              },
              altText: grade.actionsImage.altText || '',
            },
          }),
          ...(grade.outcomesImage?.image && {
            outcomesImage: {
              asset: {
                fluid: getFluidImage(grade.outcomesImage.image, { sizes: '(max-width: 600px) 100vw, 30vw' }),
              },
              altText: grade.outcomesImage.altText || '',
            },
          }),
        }))
        : [];

      return (
        <>
          <HorizonsChart
            key={block._key}
            heading={block.heading || block.title}
            description={block.description}
            actionsLabel={block.actionsLabel}
            outcomesLabel={block.outcomesLabel}
            grades={grades}
          />
        </>
      );
    }
    case 'circleChart': {
      const circles = Array.isArray(block.circles)
        ? block.circles.map((circle) => {
          const text = {
            en: circle.text,
            zh_Hans: circle.zh_HansText,
            vi: circle.viText,
          };

          return {
            ...circle,
            text: text[lang] || text.en,
          };
        })
        : [];

      return (
        <>
          <CircleChart
            key={block._key}
            heading={block.heading}
            layout={block.layout}
            circles={circles}
          />
        </>
      );
    }
    default:
      return null;
  }
}

export default function(block, parent, lang) {
  const content = renderBlock(block, parent, lang);

  if (!content) {
    return null;
  }

  return <Section spacing={block.spacing}>{content}</Section>;
}
